import type { CarrierCapabilityDto } from "@interfaces/heap-api";
import { ClientFetchService } from "@lib/helpers/clientFetchService";
import type { QueryClient } from "@tanstack/react-query";

type FetchInvalidCarrierPostalCodesParams = {
    carrierCode: string;
    postalCode: string;
};

type ValidateCarrierPostalCodeParams = FetchInvalidCarrierPostalCodesParams & {
    queryClient: QueryClient;
};

const fetchInvalidCarrierPostalCodes = async ({
    carrierCode,
    postalCode,
}: FetchInvalidCarrierPostalCodesParams): Promise<CarrierCapabilityDto> => {
    // Any newlines will be converted to uppercase by the field formatting, so we need to remove them manually.
    const carrierCodeSanitized = carrierCode
        .replace(/\\R\\N/g, "")
        .replace(/\\N/g, "")
        .trim();
    const postalCodeSanitized = postalCode
        .replace(/\\R\\N/g, "")
        .replace(/\\N/g, "")
        .trim();
    return ClientFetchService.get<CarrierCapabilityDto>(
        `/api/checkout/shipping-methods/validate-postal-code/${encodeURIComponent(carrierCodeSanitized)}/${encodeURIComponent(postalCodeSanitized)}`
    );
};

export type ValidateCarrierPostalCode = {
    isValid: boolean;
};

export const validateCarrierPostalCode = (args: ValidateCarrierPostalCodeParams) => {
    const { carrierCode, postalCode, queryClient } = args;
    const key = ["invalid-carrier-postal-code", carrierCode, postalCode?.trim()];
    const fetcher = async (): Promise<ValidateCarrierPostalCode> => {
        try {
            const response = await fetchInvalidCarrierPostalCodes({ carrierCode, postalCode });
            return {
                isValid: response?.capabilities?.homeDeliveryAvailable ?? true,
            };
        } catch {
            // In case of errors in a service we allow the user to proceed and fix the orders manually at a later point.
            return { isValid: true };
        }
    };
    return queryClient.ensureQueryData({
        queryKey: key,
        queryFn: fetcher,
        staleTime: Infinity,
        retry: 0,
    });
};
